/**
 * API client for terms and conditions acceptance.
 */

import { buildApiHeaders } from './apiUtils';

export interface TermsAcceptance {
    accepted: boolean;
    tc_agreement?: boolean;
    accepted_at?: number;
    open_datasets?: boolean;
    open_datasets_timestamp?: number | null;
    terms_version?: string;
}

export interface RecordTermsAcceptanceRequest {
    tc_agreement: boolean;
    open_datasets: boolean;
    terms_version?: string;
}

export interface RecordTermsAcceptanceResponse {
    success: boolean;
    tc_agreement: boolean;
    accepted_at: number;
    open_datasets: boolean;
    open_datasets_timestamp: number | null;
    terms_version: string;
}

/**
 * Check if the current user has accepted the terms and conditions.
 */
export async function checkTermsAcceptance(): Promise<TermsAcceptance> {
    const headers = await buildApiHeaders();
    const response = await fetch('/api/user/terms-acceptance', {
        method: 'GET',
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to check terms acceptance: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Record the current user's acceptance of terms and conditions.
 */
export async function recordTermsAcceptance(
    openDatasets: boolean,
    termsVersion: string = '1.0'
): Promise<RecordTermsAcceptanceResponse> {
    const request: RecordTermsAcceptanceRequest = {
        tc_agreement: true,
        open_datasets: openDatasets,
        terms_version: termsVersion,
    };

    const headers = await buildApiHeaders();
    const response = await fetch('/api/user/terms-acceptance', {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to record terms acceptance');
    }

    return response.json();
}
